import {
  createFirmLeadSourceV2,
  deleteFirmLeadSourceV2,
  getFirmLeadSourceMappingTargetsV2,
  getFirmLeadSourcesV2,
  updateFirmLeadSourceV2,
} from "@/lib/data/requests/firm/firm-lead-source";
import { queryKeys } from "@/lib/data/static";
import type { APIResponse, FirmLeadSource } from "@/lib/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

export const retrieveFirmLeadSourceOptions = () => {
  return {
    queryKey: [queryKeys.firmLeadSources],
    queryFn: async () => getFirmLeadSourcesV2(),
  };
};

export const retrieveFirmLeadSourceMappingTargetsOptions = () => {
  return {
    queryKey: [queryKeys.firmLeadSourceMappingTargets],
    queryFn: async () => getFirmLeadSourceMappingTargetsV2(),
  } as const;
};

export const useCreateFirmLeadSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createFirmLeadSourceV2>[0]) =>
      createFirmLeadSourceV2(data),
    onError: () => {
      toast.error("Error creating firm lead source");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmLeadSources],
      });
    },
    onSuccess: () => {
      toast.success("Firm lead source created successfully");
    },
  });
};

export const useUpdateFirmLeadSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateFirmLeadSourceV2>[0]) =>
      updateFirmLeadSourceV2(data),
    onError: () => {
      toast.error("Error updating firm lead source");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmLeadSources],
      });
    },
    onSuccess: () => {
      toast.success("Firm lead source updated successfully");
    },
  });
};

export const useDeleteFirmLeadSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteFirmLeadSourceV2>[0]) =>
      deleteFirmLeadSourceV2(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.firmLeadSources],
        (oldData: APIResponse<FirmLeadSource[]> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: oldData.data.filter((card) => card.id !== data.id),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.firmLeadSources]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.firmLeadSources],
          context.previousData,
        );
      }
      toast.error("Error deleting firm lead source");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmLeadSources],
      });
    },
    onSuccess: () => {
      toast.success("Firm lead source deleted successfully");
    },
  });
};
